/**
 * financeIntelligence.js
 *
 * Pure helpers for the Finance Intelligence screens (cashier anomalies, VAT /
 * NAV check, expenses, supplier prices). No React, no network — unit-tested in
 * `app/tests/financeIntelligence.test.js`.
 *
 * @module helpers/financeIntelligence
 */
import {formatNumber, toDateKey} from './businessAi';

/** Tabs of the screen, in display order. */
export const FINANCE_TABS = ['cashier', 'vat', 'expenses', 'suppliers'];

/** Window options (days) per tab. */
export const WINDOW_OPTIONS = {
    cashier: [7, 30, 90],
    vat: [7, 30, 90],
    expenses: [30, 90, 365],
    suppliers: [30, 90, 180],
};

/** Backend `reason` values with a dedicated empty state. */
export const KNOWN_REASONS = ['NO_EMPLOYEE_ATTRIBUTION', 'NO_ORDERS', 'INSUFFICIENT_SAMPLE', 'FEW_PEERS'];

/** VAT issue codes the backend emits (anything else renders as the raw code). */
export const VAT_ISSUE_CODES = [
    'DATE_FUTURE', 'DATE_VERY_OLD', 'DUPLICATE_INVOICE_NUMBER', 'DUPLICATE_SUPPLIER_AMOUNT_DATE', 'HEADER_GROSS_INCONSISTENT',
    'LINE_GROSS_MISMATCH', 'NAV_STATUS_ABORTED', 'NAV_STATUS_ERROR', 'NAV_STATUS_STUCK', 'PARTNER_TAX_NUMBER_INVALID',
    'PARTNER_TAX_NUMBER_MISSING', 'ROUNDING_DRIFT', 'VAT_AMOUNT_MISMATCH', 'VAT_CATEGORY_CONFLICT', 'VAT_CATEGORY_INVALID',
    'VAT_CATEGORY_MISSING', 'VAT_RATE_INVALID', 'VAT_RATE_MISSING',
];

/** VAT fix-hint keys the backend emits. */
export const VAT_HINTS = [
    'set_vat_rate', 'fix_vat_rate', 'recalculate_vat_amount', 'recalculate_line_gross', 'set_vat_exemption_category',
    'recompute_header_totals', 'accept_rounding', 'correct_partner_tax_number', 'add_partner_tax_number', 'resubmit_to_nav',
    'check_nav_status_manually', 'check_issue_date', 'merge_or_cancel_duplicate',
];

/** Cashier reason keys (`reasons[].key`) the backend emits. */
export const CASHIER_REASON_KEYS = ['VOID_RATE', 'CANCELLED_AFTER_PAYMENT', 'REFUND_VALUE', 'DISCOUNT_RATE', 'AVG_DISCOUNT_PCT', 'OFF_HOURS_RATE', 'CASH_DIFFERENCE'];

/** Risk score thresholds, used only when the server sends a score without a level. */
export const RISK_WATCH_SCORE = 35;
export const RISK_REVIEW_SCORE = 65;

/**
 * Inclusive `from`/`to` date keys of a "last N days" window (device timezone).
 * @param {number} days window length (1 → today only).
 * @param {Date} [now] reference time.
 * @returns {{from: string, to: string}} date keys.
 */
export const windowToRange = (days, now = new Date()) => {
    const span = Math.max(1, Math.floor(Number(days)) || 1);
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (span - 1));

    return {from: toDateKey(start), to: toDateKey(now)};
};

/**
 * Risk level of an employee: the server's level wins, the score is the fallback.
 * @param {{riskLevel?: string, riskScore?: *}} employee employee row.
 * @returns {'normal'|'watch'|'review'} level.
 */
export const classifyRiskLevel = (employee) => {
    const level = employee?.riskLevel;

    if (level === 'normal' || level === 'watch' || level === 'review') return level;
    const score = Number(employee?.riskScore);

    if (!Number.isFinite(score)) return 'normal';
    if (score >= RISK_REVIEW_SCORE) return 'review';

    return score >= RISK_WATCH_SCORE ? 'watch' : 'normal';
};

/**
 * Calm display meta of a risk level (no red: it is a statistical hint).
 * @param {string} level `normal` | `watch` | `review`.
 * @returns {{key: string, tone: string, color: string, rank: number}} meta (unknown → normal).
 */
export const getRiskMeta = (level) => {
    switch (level) {
        case 'review':
            return {key: 'review', tone: 'warning', color: '#F97316', rank: 0};
        case 'watch':
            return {key: 'watch', tone: 'info', color: '#3B82F6', rank: 1};
        default:
            return {key: 'normal', tone: 'success', color: '#16A34A', rank: 2};
    }
};

/**
 * Severity meta for findings, VAT issues and cashier reasons. Accepts the
 * `error`/`high` aliases; unknown → info.
 * @param {string} severity raw severity.
 * @returns {{key: 'info'|'warning'|'critical', tone: string, color: string, rank: number}} meta.
 */
export const getFinanceSeverityMeta = (severity) => {
    const value = String(severity || '').toLowerCase();

    if (value === 'critical' || value === 'error' || value === 'high') return {key: 'critical', tone: 'danger', color: '#EF4444', rank: 0};
    if (value === 'warning' || value === 'medium') return {key: 'warning', tone: 'warning', color: '#F97316', rank: 1};

    return {key: 'info', tone: 'info', color: '#3B82F6', rank: 2};
};

/**
 * Locale key for a VAT issue code, or `null` for unknown codes.
 * @param {string} code issue code.
 * @returns {?string} i18n key.
 */
export const vatCodeKey = (code) => (VAT_ISSUE_CODES.includes(code) ? `finint.vat_code_${code}` : null);

/**
 * Locale key for a VAT fix hint, or `null` for unknown hints.
 * @param {string} hint hint key.
 * @returns {?string} i18n key.
 */
export const vatHintKey = (hint) => (VAT_HINTS.includes(hint) ? `finint.vat_hint_${hint}` : null);

/**
 * Localized VAT issue message; unknown code → the raw code.
 * @param {Function} t i18n `t`.
 * @param {string} code issue code.
 * @returns {string} message.
 */
export const vatCodeMessage = (t, code) => {
    const key = vatCodeKey(code);

    return key ? t(key) : String(code || '');
};

/**
 * Localized VAT fix hint; unknown hint → empty string.
 * @param {Function} t i18n `t`.
 * @param {string} hint hint key.
 * @returns {string} hint text.
 */
export const vatHintMessage = (t, hint) => {
    const key = vatHintKey(hint);

    return key ? t(key) : '';
};

/**
 * Locale key of a cashier reason, or `null` when unknown.
 * @param {string} key reason key.
 * @returns {?string} i18n key.
 */
export const cashierReasonKey = (key) => (CASHIER_REASON_KEYS.includes(key) ? `finint.reason_${key}` : null);

/**
 * Locale key of a flagged-order reason, or `null` when unknown.
 * @param {string} reason `STORNO` | `CANCELLED_AFTER_PAYMENT` | `HIGH_DISCOUNT` | `OFF_HOURS`.
 * @returns {?string} i18n key.
 */
export const flaggedOrderReasonKey = (reason) => (['STORNO', 'CANCELLED_AFTER_PAYMENT', 'HIGH_DISCOUNT', 'OFF_HOURS'].includes(reason) ? `finint.order_${reason}` : null);

/**
 * Locale key of an empty-state reason (generic fallback for unknown / missing).
 * @param {?string} reason backend reason.
 * @returns {string} i18n key (`finint.empty_<REASON>` or `finint.empty_generic`).
 */
export const emptyReasonKey = (reason) => (KNOWN_REASONS.includes(reason) ? `finint.empty_${reason}` : 'finint.empty_generic');

/**
 * Whether a failed cashier call means "not allowed for your role".
 * @param {?{status?: ?number, error?: ?string}} result `{ok:false}` result of the API client.
 * @returns {boolean} true on HTTP 403.
 */
export const isRestrictedResult = (result) => !!result && !result.ok && (result.status === 403 || /^API 403\b/.test(String(result.error || '')));

/**
 * Format a percentage ratio (`0.123` → `12,3%`) or an already-percent value.
 * @param {*} value raw value.
 * @param {{ratio?: boolean}} [options] `ratio` when the value is 0..1.
 * @returns {string} text, `—` without a number.
 */
export const formatPercent = (value, {ratio = false} = {}) => {
    if (value === null || value === undefined || value === '' || !Number.isFinite(Number(value))) return '—';
    const pct = ratio ? Number(value) * 100 : Number(value);

    return `${formatNumber(pct, 1)}%`;
};

/**
 * Money text with a currency code, e.g. `1 234 567 HUF`.
 * @param {*} amount amount.
 * @param {?string} currency currency code.
 * @returns {string} text, `—` without a number.
 */
export const formatMoney = (amount, currency) => {
    if (amount === null || amount === undefined || amount === '' || !Number.isFinite(Number(amount))) return '—';
    const num = Number(amount);
    const text = formatNumber(num, Math.abs(num) < 1000 && !Number.isInteger(num) ? 2 : 0);

    return currency ? `${text} ${currency}` : text;
};

/**
 * Sort findings worst-first (stable on equal severity) and cap them.
 * @param {Array<object>} findings findings.
 * @param {number} [max=5] cap.
 * @returns {Array<object>} sorted copy.
 */
export const sortFindings = (findings, max = 5) => (Array.isArray(findings) ? findings : [])
    .map((finding, index) => ({finding, index}))
    .filter(({finding}) => finding && (finding.title || finding.message))
    .sort((a, b) => getFinanceSeverityMeta(a.finding.severity).rank - getFinanceSeverityMeta(b.finding.severity).rank || a.index - b.index)
    .slice(0, max)
    .map(({finding}) => finding);

/**
 * Employees sorted for display: eligible first, then by risk level and score.
 * @param {Array<object>} employees employee rows.
 * @returns {Array<object>} sorted copy.
 */
export const sortEmployees = (employees) => (Array.isArray(employees) ? employees : [])
    .map((employee, index) => ({employee, index}))
    .sort((a, b) => {
        const ea = a.employee.eligible === false ? 1 : 0;
        const eb = b.employee.eligible === false ? 1 : 0;

        if (ea !== eb) return ea - eb;
        const ra = getRiskMeta(classifyRiskLevel(a.employee)).rank;
        const rb = getRiskMeta(classifyRiskLevel(b.employee)).rank;

        return ra - rb || (Number(b.employee.riskScore) || 0) - (Number(a.employee.riskScore) || 0) || a.index - b.index;
    })
    .map(({employee}) => employee);

/**
 * Number of employees at the `review` level.
 * @param {?{employees?: Array<object>}} data cashier-anomalies response.
 * @returns {number} count.
 */
export const countReviewFlags = (data) => (Array.isArray(data?.employees) ? data.employees : [])
    .filter((employee) => employee?.eligible !== false && classifyRiskLevel(employee) === 'review').length;

/**
 * Biggest saving potential among the supplier-price response.
 * @param {?{summary?: object}} data supplier-prices response.
 * @returns {?{amount: number, currency: ?string}} top saving, or `null` when none.
 */
export const topSupplierSaving = (data) => {
    const summary = data?.summary;
    const byCurrency = Array.isArray(summary?.byCurrency) ? summary.byCurrency : [];
    const best = byCurrency
        .map((row) => ({amount: Number(row?.totalSavingPotential ?? row?.savingPotential ?? row?.amount), currency: row?.currency || null}))
        .filter((row) => Number.isFinite(row.amount) && row.amount > 0)
        .sort((a, b) => b.amount - a.amount)[0];

    if (best) return best;
    const total = Number(summary?.totalSavingPotential);

    return Number.isFinite(total) && total > 0 ? {amount: total, currency: summary?.currency || null} : null;
};

/**
 * Bar ratio (0..1) of a category share. Accepts a 0..1 ratio or a 0..100 percent.
 * @param {*} share raw share.
 * @returns {number} 0..1.
 */
export const shareBarRatio = (share) => {
    const value = Number(share);

    if (!Number.isFinite(value) || value <= 0) return 0;

    return Math.min(1, value > 1 ? value / 100 : value);
};

/**
 * Whether a supplier-price item has a cheaper alternative worth showing.
 * @param {{savingPct?: *, savingPotential?: *}} item supplier-price item.
 * @returns {boolean} true when there is a positive saving.
 */
export const hasSaving = (item) => Number(item?.savingPct) > 0 || Number(item?.savingPotential) > 0;

/** Cashier reason keys whose `value` / `peer` are percentages. */
const PERCENT_REASON_KEYS = ['VOID_RATE', 'CANCELLED_AFTER_PAYMENT', 'REFUND_VALUE', 'DISCOUNT_RATE', 'AVG_DISCOUNT_PCT', 'OFF_HOURS_RATE'];

/**
 * Format a cashier reason value / peer value by its key.
 * @param {string} key reason key.
 * @param {*} value raw value.
 * @returns {string} text (`12,5%` for rates, a grouped number otherwise, `—` when missing).
 */
export const formatReasonValue = (key, value) => (PERCENT_REASON_KEYS.includes(key) ? formatPercent(value) : formatNumber(value, 1));
